import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ZoomIn } from "lucide-react";
import { Lightbox, useLightbox } from "@/components/Lightbox";
import { SITE_IMAGES } from "@/lib/site-data";
import { DotGrid, FloatingShape, DiagonalLines, NumberWatermark } from "@/components/Deco";

const FILTERS = [
  { id: "all", label: "Összes" },
  { id: "truck", label: "Teherautó" },
  { id: "repair", label: "Javítás" },
  { id: "ad", label: "Reklám" },
  { id: "tent", label: "Sátor" },
  { id: "awning", label: "Árnyékoló" },
];

const buildItems = () => {
  const tag = (urls, type, labelPrefix) =>
    urls.map((url, i) => ({ url, type, label: `${labelPrefix} #${i + 1}` }));
  return [
    ...tag(SITE_IMAGES.truckGallery, "truck", "Teherautó / kamion ponyva"),
    ...tag(SITE_IMAGES.repairGallery, "repair", "Ponyva javítás / csere"),
    ...tag(SITE_IMAGES.adGallery, "ad", "Reklámdekoráció"),
    ...tag(SITE_IMAGES.tentGallery, "tent", "Sátor / előtető"),
    ...tag(SITE_IMAGES.awningGallery, "awning", "Teraszárnyékoló"),
  ];
};

export const Gallery = () => {
  const all = useMemo(() => buildItems(), []);
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(
    () => (filter === "all" ? all : all.filter((i) => i.type === filter)),
    [filter, all],
  );
  const lb = useLightbox(filtered);

  return (
    <section
      id="galeria"
      data-testid="gallery-section"
      className="section-pad bg-[#F8FAFC] relative overflow-hidden"
    >
      <FloatingShape className="-top-32 -left-32 w-96 h-96" color="#1E3E62" />
      <DiagonalLines className="absolute top-0 left-0 right-0 h-2 text-[#EA580C]" opacity={0.4} />
      <NumberWatermark n="04" className="hidden md:block top-12 -left-12" />
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-5xl"
        >
          <div>
            <span className="eyebrow">Referenciák</span>
            <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Munkáink képekben.
            </h2>
          </div>
          <p className="text-gray-600 text-base leading-relaxed md:max-w-md">
            Ügyfeleink között a Waberer's, Viarent, Schmitz és számtalan
            magyar fuvarozó. Kattintson a képekre a részletekért.
          </p>
        </motion.div>

        {/* Filter tabs */}
        <div
          data-testid="gallery-filters"
          className="mt-10 flex flex-wrap gap-2"
        >
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`gallery-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                filter === f.id
                  ? "bg-[#0B192C] text-white shadow-md"
                  : "bg-white text-gray-700 border border-gray-200 hover:border-[#EA580C] hover:text-[#EA580C]"
              }`}
            >
              {f.label}
              <span
                className={`ml-2 text-xs ${
                  filter === f.id ? "text-white/70" : "text-gray-400"
                }`}
              >
                {f.id === "all"
                  ? all.length
                  : all.filter((i) => i.type === f.id).length}
              </span>
            </button>
          ))}
        </div>

        {/* Masonry-ish grid */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((img, i) => (
              <motion.button
                key={`${img.type}-${i}`}
                layout
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.35, delay: (i % 8) * 0.04 }}
                onClick={() => lb.openAt(i)}
                data-testid={`gallery-item-${i}`}
                className={`group relative overflow-hidden rounded-lg bg-gray-200 ${
                  i === 0 || i === 5
                    ? "row-span-2 aspect-[3/4]"
                    : "aspect-[4/3]"
                }`}
              >
                <img
                  src={img.url}
                  alt={img.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/85 via-[#0B192C]/0 to-[#0B192C]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 text-[#0B192C] flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-75 transition-all">
                  <ZoomIn className="w-4 h-4" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-white font-heading font-semibold text-xs md:text-sm">
                    {img.label}
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <Lightbox
        images={filtered}
        open={lb.open}
        index={lb.index}
        onClose={lb.close}
        onNav={lb.nav}
      />
    </section>
  );
};

export default Gallery;
